if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.stage = this.querySelector('[data-product-zoom-stage]');
        this.controls = this.querySelector('[data-product-zoom-controls]');
        this.zoomInButton = this.querySelector('[data-product-zoom-in]');
        this.zoomOutButton = this.querySelector('[data-product-zoom-out]');
        this.zoomResetButton = this.querySelector('[data-product-zoom-reset]');
        this.zoomValue = this.querySelector('[data-product-zoom-value]');
        this.hint = this.querySelector('[data-product-zoom-hint]');
        this.previousButton = this.querySelector('[data-product-modal-prev]');
        this.nextButton = this.querySelector('[data-product-modal-next]');

        this.activeMedia = null;
        this.scale = 1;
        this.panX = 0;
        this.panY = 0;
        this.minScale = 1;
        this.maxScale = 4;
        this.scaleStep = 0.25;
        this.pointers = new Map();
        this.dragStart = null;
        this.swipeStart = null;
        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;
        this.pinchCenter = null;

        this.bindZoomEvents();
      }

      bindZoomEvents() {
        if (!this.stage) return;

        /* The base modal closes on pointerup inside image areas. Stop that here. */
        this.querySelector('.product-media-modal__dialog')?.addEventListener('pointerup', (event) => {
          event.stopPropagation();
        });

        this.zoomInButton?.addEventListener('click', () => this.zoomBy(this.scaleStep));
        this.zoomOutButton?.addEventListener('click', () => this.zoomBy(-this.scaleStep));
        this.zoomResetButton?.addEventListener('click', () => this.resetZoom());
        this.previousButton?.addEventListener('click', () => this.changeImage(-1));
        this.nextButton?.addEventListener('click', () => this.changeImage(1));

        this.stage.addEventListener(
          'wheel',
          (event) => {
            if (!this.isZoomableImage()) return;
            event.preventDefault();
            const amount = event.deltaY < 0 ? this.scaleStep : -this.scaleStep;
            this.zoomBy(amount, { x: event.clientX, y: event.clientY });
          },
          { passive: false }
        );

        this.stage.addEventListener('dblclick', (event) => {
          if (!this.isZoomableImage()) return;
          event.preventDefault();

          if (this.scale > 1) {
            this.resetZoom();
          } else {
            this.setScale(2, { x: event.clientX, y: event.clientY });
          }
        });

        this.stage.addEventListener('dragstart', (event) => event.preventDefault());
        this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.stage.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.stage.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.stage.addEventListener('pointercancel', this.onPointerUp.bind(this));

        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;

          if (event.key === '+' || event.key === '=') {
            event.preventDefault();
            this.zoomBy(this.scaleStep);
          } else if (event.key === '-') {
            event.preventDefault();
            this.zoomBy(-this.scaleStep);
          } else if (event.key === '0') {
            event.preventDefault();
            this.resetZoom();
          } else if (event.key === 'ArrowLeft' && this.scale === 1) {
            event.preventDefault();
            this.changeImage(-1);
          } else if (event.key === 'ArrowRight' && this.scale === 1) {
            event.preventDefault();
            this.changeImage(1);
          }
        });
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      hide() {
        this.resetZoom();
        this.pointers.clear();
        super.hide();
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        if (!mediaId) return;

        this.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('active', element.getAttribute('data-media-id') === mediaId);
        });

        const activeMedia = this.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector?.('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        this.setActiveMedia(activeMedia);

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      setActiveMedia(media) {
        this.activeMedia = media;
        this.resetZoom();

        const isImage = this.isZoomableImage();
        if (this.controls) this.controls.hidden = !isImage;
        if (this.hint) this.hint.hidden = !isImage;

        const images = this.getImages();
        const showNavigation = images.length > 1;
        if (this.previousButton) this.previousButton.hidden = !showNavigation;
        if (this.nextButton) this.nextButton.hidden = !showNavigation;

        requestAnimationFrame(() => {
          this.activeMedia?.focus?.({ preventScroll: true });
          this.updateControls();
        });
      }

      getImages() {
        return Array.from(this.stage?.querySelectorAll(':scope > img[data-media-id]') || []);
      }

      changeImage(direction) {
        const images = this.getImages();
        if (images.length < 2) return;

        let currentIndex = images.indexOf(this.activeMedia);
        if (currentIndex < 0) currentIndex = 0;

        const nextIndex = (currentIndex + direction + images.length) % images.length;
        this.querySelectorAll('[data-media-id]').forEach((element) => element.classList.remove('active'));
        images[nextIndex].classList.add('active');
        this.setActiveMedia(images[nextIndex]);
      }

      isZoomableImage() {
        return this.activeMedia?.tagName === 'IMG';
      }

      zoomBy(amount, origin) {
        this.setScale(this.scale + amount, origin);
      }

      setScale(nextScale, origin) {
        if (!this.isZoomableImage()) return;

        const clampedScale = Math.min(this.maxScale, Math.max(this.minScale, nextScale));
        if (clampedScale === this.scale) return;

        if (origin && this.stage) {
          const stageRect = this.stage.getBoundingClientRect();
          const originX = origin.x - (stageRect.left + stageRect.width / 2);
          const originY = origin.y - (stageRect.top + stageRect.height / 2);
          const ratio = clampedScale / this.scale;

          this.panX = originX - (originX - this.panX) * ratio;
          this.panY = originY - (originY - this.panY) * ratio;
        }

        this.scale = clampedScale;

        if (this.scale === 1) {
          this.panX = 0;
          this.panY = 0;
        }

        this.clampPan();
        this.applyTransform();
      }

      resetZoom() {
        this.scale = 1;
        this.panX = 0;
        this.panY = 0;
        this.classList.remove('is-zoomed', 'is-dragging');
        this.applyTransform();
      }

      clampPan() {
        if (!this.activeMedia || !this.stage || this.scale <= 1) {
          this.panX = 0;
          this.panY = 0;
          return;
        }

        const maxX = Math.max(0, (this.activeMedia.offsetWidth * this.scale - this.stage.clientWidth) / 2 + 24);
        const maxY = Math.max(0, (this.activeMedia.offsetHeight * this.scale - this.stage.clientHeight) / 2 + 24);

        this.panX = Math.min(maxX, Math.max(-maxX, this.panX));
        this.panY = Math.min(maxY, Math.max(-maxY, this.panY));
      }

      applyTransform() {
        if (this.activeMedia?.tagName === 'IMG') {
          this.activeMedia.style.transform = `translate3d(${this.panX}px, ${this.panY}px, 0) scale(${this.scale})`;
        }

        this.classList.toggle('is-zoomed', this.scale > 1);
        this.updateControls();
      }

      updateControls() {
        if (this.zoomValue) this.zoomValue.textContent = `${Math.round(this.scale * 100)}%`;
        if (this.zoomOutButton) this.zoomOutButton.disabled = this.scale <= this.minScale;
        if (this.zoomInButton) this.zoomInButton.disabled = this.scale >= this.maxScale;
      }

      getPointerDistance() {
        const points = Array.from(this.pointers.values());
        if (points.length < 2) return 0;
        return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      }

      getPointerCenter() {
        const points = Array.from(this.pointers.values());
        if (points.length < 2) return null;
        return {
          x: (points[0].x + points[1].x) / 2,
          y: (points[0].y + points[1].y) / 2,
        };
      }

      onPointerDown(event) {
        if (!this.isZoomableImage()) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        this.stage.setPointerCapture?.(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 1) {
          this.dragStart = {
            x: event.clientX,
            y: event.clientY,
            panX: this.panX,
            panY: this.panY,
          };
          this.swipeStart = { x: event.clientX, y: event.clientY };
          if (this.scale > 1) this.classList.add('is-dragging');
        } else if (this.pointers.size === 2) {
          this.pinchStartDistance = this.getPointerDistance();
          this.pinchStartScale = this.scale;
          this.pinchCenter = this.getPointerCenter();
          this.classList.add('is-dragging');
        }
      }

      onPointerMove(event) {
        if (!this.pointers.has(event.pointerId) || !this.isZoomableImage()) return;

        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size >= 2) {
          const distance = this.getPointerDistance();
          const center = this.getPointerCenter() || this.pinchCenter;
          if (this.pinchStartDistance > 0) {
            const nextScale = this.pinchStartScale * (distance / this.pinchStartDistance);
            this.setScale(nextScale, center);
          }
          event.preventDefault();
          return;
        }

        if (this.scale > 1 && this.dragStart) {
          this.panX = this.dragStart.panX + (event.clientX - this.dragStart.x);
          this.panY = this.dragStart.panY + (event.clientY - this.dragStart.y);
          this.clampPan();
          this.applyTransform();
          event.preventDefault();
        }
      }

      onPointerUp(event) {
        const pointer = this.pointers.get(event.pointerId)
          ? { x: event.clientX, y: event.clientY }
          : null;
        this.pointers.delete(event.pointerId);
        this.stage.releasePointerCapture?.(event.pointerId);

        if (this.pointers.size === 0) {
          this.classList.remove('is-dragging');

          if (this.scale === 1 && this.swipeStart && pointer) {
            const deltaX = pointer.x - this.swipeStart.x;
            const deltaY = pointer.y - this.swipeStart.y;

            if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY)) {
              this.changeImage(deltaX < 0 ? 1 : -1);
            }
          }

          this.dragStart = null;
          this.swipeStart = null;
          this.pinchStartDistance = 0;
          this.pinchCenter = null;
        } else if (this.pointers.size === 1) {
          const remaining = Array.from(this.pointers.values())[0];
          this.dragStart = {
            x: remaining.x,
            y: remaining.y,
            panX: this.panX,
            panY: this.panY,
          };
        }
      }
    }
  );
}
